/**
 * fb-token-import-dialog.tsx — Thêm Fanpage thủ công bằng User Access Token.
 *
 * Dùng khi không đi được luồng OAuth (page do tài khoản Facebook khác quản
 * trị, hoặc muốn chọn đúng vài page). Hai bước: dán token → chọn page → kết
 * nối. Token page ở lại máy chủ; trình duyệt chỉ thấy tên page.
 */
import { useState } from 'react'
import { toast } from 'sonner'
import { Facebook, Loader2, KeyRound, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { apiError } from '@/lib/api-client'
import { cn, initials } from '@/lib/utils'
import { useFbTokenImportPages, useFbTokenImportConfirm, type FbTokenImportPage } from '@/hooks/use-integrations'

interface Props {
  open: boolean
  onOpenChange: (v: boolean) => void
  /** externalPageId của các page đã kết nối — đánh dấu để khỏi chọn lại. */
  daKetNoi: Set<string>
}

export function FbTokenImportDialog({ open, onOpenChange, daKetNoi }: Props) {
  const [token, setToken] = useState('')
  const [importId, setImportId] = useState<string | null>(null)
  const [pages, setPages] = useState<FbTokenImportPage[]>([])
  const [chon, setChon] = useState<Set<string>>(() => new Set())
  const layPages = useFbTokenImportPages()
  const xacNhan = useFbTokenImportConfirm()

  function reset() {
    setToken(''); setImportId(null); setPages([]); setChon(new Set())
  }
  function dong() { reset(); onOpenChange(false) }

  async function docToken() {
    const t = token.trim()
    if (!t) return
    try {
      const r = await layPages.mutateAsync(t)
      setImportId(r.importId)
      setPages(r.pages)
      // Mặc định tick page chưa kết nối.
      setChon(new Set(r.pages.filter((p) => !daKetNoi.has(p.id)).map((p) => p.id)))
      setToken('') // không giữ token trong state lâu hơn cần thiết
      if (r.pages.length === 0) toast.info('Token này không quản trị Fanpage nào.')
    } catch (err) { toast.error(apiError(err)) }
  }

  async function ketNoi() {
    if (!importId || chon.size === 0) return
    try {
      const r = await xacNhan.mutateAsync({ importId, pageIds: [...chon] })
      if (r.connected > 0) toast.success(`Đã kết nối ${r.connected} Fanpage`)
      for (const e of r.errors ?? []) toast.error(e)
      if (r.connected > 0) dong()
    } catch (err) {
      toast.error(apiError(err))
      // Phiên import hết hạn → về bước dán token.
      if (/expired|not found/i.test(String((err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? ''))) {
        setImportId(null); setPages([])
      }
    }
  }

  const toggle = (id: string) => setChon((cu) => { const m = new Set(cu); if (m.has(id)) m.delete(id); else m.add(id); return m })

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : dong())}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="h-4 w-4" /> Thêm Fanpage bằng token</DialogTitle>
        </DialogHeader>

        {!importId ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Dán <strong>User Access Token</strong> của tài khoản Facebook đang quản trị page (lấy từ Meta Business Suite hoặc Graph API Explorer với quyền{' '}
              <code className="rounded bg-muted px-1 text-xs">pages_show_list, pages_messaging, pages_manage_metadata</code>). Hệ thống sẽ liệt kê các page tài khoản đó quản lý để anh/chị chọn.
            </p>
            <textarea
              autoFocus
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="EAAB…"
              spellCheck={false}
              className="min-h-[96px] w-full resize-y rounded-md border bg-background p-2 font-mono text-xs outline-none focus:ring-2 focus:ring-primary/30"
            />
            <p className="text-[11px] text-muted-foreground">
              Token chỉ dùng một lần để đọc danh sách page và lấy token riêng của từng page; không lưu token người dùng.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={dong}>Huỷ</Button>
              <Button onClick={() => void docToken()} disabled={!token.trim() || layPages.isPending}>
                {layPages.isPending && <Loader2 className="animate-spin" />} Đọc danh sách page
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Chọn page cần kết nối ({chon.size}/{pages.length}).</p>
            <ul className="max-h-72 divide-y overflow-y-auto rounded-md border">
              {pages.map((p) => {
                const co = daKetNoi.has(p.id)
                const tick = chon.has(p.id)
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => toggle(p.id)}
                      className={cn('flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-muted', tick && 'bg-primary/5')}
                    >
                      <span className={cn('flex h-5 w-5 shrink-0 items-center justify-center rounded border-2', tick ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/40')}>
                        {tick && <Check className="h-3.5 w-3.5" />}
                      </span>
                      <Avatar className="h-8 w-8">
                        {p.avatarUrl && <AvatarImage src={p.avatarUrl} alt={p.name} />}
                        <AvatarFallback><Facebook className="h-4 w-4" /></AvatarFallback>
                      </Avatar>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">{p.name || initials(p.id)}</span>
                        <span className="block text-[11px] text-muted-foreground">ID {p.id}{co ? ' · đã kết nối — chọn để làm mới token' : ''}</span>
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
            <DialogFooter>
              <Button variant="ghost" onClick={() => { setImportId(null); setPages([]) }}>Dán token khác</Button>
              <Button onClick={() => void ketNoi()} disabled={chon.size === 0 || xacNhan.isPending}>
                {xacNhan.isPending && <Loader2 className="animate-spin" />} Kết nối {chon.size > 0 ? chon.size : ''} page
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
